import { ArrowUpRight, Clock, Tag } from "lucide-react";
import { useState } from "react";
import type { PublicOffer } from "@/pages/offers/shared/publicOffer.types";
import { OfferTimeLeftLabel } from "@/pages/offers/shared/PublicOfferMedia";

interface MemberOfferCardProps {
  offer: PublicOffer;
  onOpenDetail: (offer: PublicOffer) => void;
}

export function MemberOfferCard({ offer, onOpenDetail }: MemberOfferCardProps) {
  const [imageFailed, setImageFailed] = useState(false);
  // Card uses secondary (merchant) image; modal keeps the main offer image
  const heroUrl = offer.cardImageUrl || offer.detailImageUrl;
  const showPhoto = Boolean(heroUrl) && !imageFailed;
  const category = (offer.categoryName || "Offer").trim();

  return (
    <button
      type="button"
      onClick={() => onOpenDetail(offer)}
      className="group flex h-[168px] w-full overflow-hidden rounded-2xl border border-amber-200/60 bg-gradient-to-br from-white via-white to-amber-50/40 text-left shadow-[0_1px_3px_rgba(15,23,42,0.06)] transition duration-300 hover:-translate-y-0.5 hover:border-amber-300/80 hover:shadow-[0_8px_24px_rgba(180,83,9,0.12)] sm:h-[180px]"
    >
      {/* Left — image (50%), fixed height via parent */}
      <div className="relative h-full w-1/2 shrink-0 overflow-hidden bg-amber-50">
        {showPhoto ? (
          <img
            src={heroUrl}
            alt={offer.imageAlt || offer.title}
            loading="lazy"
            decoding="async"
            onError={() => setImageFailed(true)}
            className="absolute inset-0 h-full w-full object-contain transition duration-500 group-hover:scale-[1.04]"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-amber-200 via-amber-100 to-slate-100">
            <Tag className="h-8 w-8 text-amber-600/70" aria-hidden />
          </div>
        )}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-black/15" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-slate-900/55 to-transparent" />
        <span className="absolute bottom-2.5 left-2.5 z-10 inline-flex max-w-[calc(100%-1.25rem)] truncate rounded-md bg-white/90 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-800 shadow-sm backdrop-blur-sm ring-1 ring-amber-200/60">
          {category}
        </span>
      </div>

      {/* Right — content (50%) */}
      <div className="relative flex h-full w-1/2 min-w-0 flex-col justify-between gap-2 overflow-hidden p-3.5 sm:p-4">
        <div className="min-h-0 min-w-0 flex-1 space-y-1.5 overflow-hidden">
          {offer.merchantName ? (
            <p className="truncate text-[11px] font-medium uppercase tracking-[0.12em] text-amber-700/80">
              {offer.merchantName}
            </p>
          ) : null}
          <h3 className="line-clamp-2 text-[15px] font-semibold leading-snug tracking-tight text-slate-900 sm:text-base">
            {offer.title}
          </h3>
          {offer.description ? (
            <p className="line-clamp-2 text-xs leading-relaxed text-slate-500 sm:text-[13px]">
              {offer.description}
            </p>
          ) : null}
        </div>

        <div className="flex shrink-0 items-center justify-between gap-2 border-t border-amber-100/80 pt-2.5">
          <span className="inline-flex min-w-0 items-center gap-1.5 text-[11px] font-medium text-slate-600">
            <Clock className="h-3.5 w-3.5 shrink-0 text-amber-600" aria-hidden />
            <span className="truncate">
              <OfferTimeLeftLabel
                expiresAtMillis={offer.expiresAtMillis}
                variant="card"
              />
            </span>
          </span>
          <span className="inline-flex shrink-0 items-center gap-0.5 rounded-lg bg-amber-500/10 px-2 py-1 text-[11px] font-semibold text-amber-800 transition group-hover:bg-amber-500/20">
            View
            <ArrowUpRight className="h-3.5 w-3.5" aria-hidden />
          </span>
        </div>
      </div>
    </button>
  );
}
